import { CheckCircle2, Circle, UserRound } from 'lucide-react'
import { type ReactNode } from 'react'

import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from '#app/components/ui/alert-dialog.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { ListItem } from '#app/components/ui/list/item.tsx'
import { cn } from '#app/utils/misc.tsx'
import { formatPhoneNumber } from '#app/utils/phone.ts'

type ContactCardProps = {
	readonly name: string
	readonly title?: string
	readonly email?: string
	readonly phone?: string
	readonly isSelected?: boolean
	readonly showRemoveAction?: boolean
	readonly contextLabel?: string
	readonly onClick?: () => void
	readonly onRemove?: () => void
	readonly removeLabel?: string
	readonly removeConfirmTitle?: string
	readonly removeConfirmDescription?: string
	readonly className?: string
	readonly dragHandle?: ReactNode
	readonly ariaLabel?: string
}

const computeContactPhone = (value?: string): string | undefined => {
	if (typeof value !== 'string') return undefined
	const formatted = formatPhoneNumber(value)
	if (formatted && formatted.trim().length > 0) return formatted
	const fallback = value.trim()
	return fallback.length > 0 ? fallback : undefined
}

export function ContactCard({
	name,
	title,
	email,
	phone,
	isSelected,
	showRemoveAction = true,
	contextLabel: _contextLabel,
	onClick,
	onRemove,
	removeLabel = 'Remove',
	removeConfirmTitle = 'Remove this contact?',
	removeConfirmDescription = 'This contact will be removed from the list.',
	className,
	dragHandle,
	ariaLabel,
}: ContactCardProps) {
	const trimmedName = name.trim() || 'Unnamed contact'
	const formattedPhone = computeContactPhone(phone)
	const details = [title, email, formattedPhone]
		.map((value) => value?.trim())
		.filter((value): value is string => Boolean(value && value.length))
	const description =
		details.length > 0
			? details.slice(0, 2).join(' • ')
			: 'Add email or phone details.'

	const listItemClassName = cn(
		isSelected ? 'border-primary bg-primary/10' : undefined,
		className,
	)

	const showSelectionIndicator = typeof isSelected === 'boolean'

	const selectionIndicator = showSelectionIndicator ? (
		isSelected ? (
			<CheckCircle2 className="text-primary h-5 w-5" aria-hidden="true" />
		) : (
			<Circle className="text-muted-foreground h-5 w-5" aria-hidden="true" />
		)
	) : null

	const shouldRenderRemove = showRemoveAction && typeof onRemove === 'function'

	const actions =
		dragHandle || selectionIndicator || shouldRenderRemove ? (
			<div className="flex items-center gap-2">
				{dragHandle ? (
					<div className="text-muted-foreground" data-dnd-handle>
						{dragHandle}
					</div>
				) : null}
				{selectionIndicator}
				{shouldRenderRemove ? (
					<AlertDialog>
						<AlertDialogTrigger asChild>
							<Button
								type="button"
								variant="ghost"
								size="icon"
								className="text-destructive hover:text-destructive focus-visible:text-destructive"
								aria-label={
									ariaLabel ? `${ariaLabel} — ${removeLabel}` : removeLabel
								}
							>
								<span aria-hidden="true">×</span>
							</Button>
						</AlertDialogTrigger>
						<AlertDialogContent>
							<AlertDialogHeader>
								<AlertDialogTitle>{removeConfirmTitle}</AlertDialogTitle>
								<AlertDialogDescription>
									{removeConfirmDescription}
								</AlertDialogDescription>
							</AlertDialogHeader>
							<AlertDialogFooter>
								<AlertDialogCancel>Cancel</AlertDialogCancel>
								<AlertDialogAction onClick={onRemove}>
									{removeLabel}
								</AlertDialogAction>
							</AlertDialogFooter>
						</AlertDialogContent>
					</AlertDialog>
				) : null}
			</div>
		) : undefined

	return (
		<ListItem
			className={listItemClassName}
			avatar={{
				alt: trimmedName,
				fallback: (
					<UserRound
						className="text-muted-foreground h-3.5 w-3.5"
						aria-hidden="true"
					/>
				),
			}}
			title={trimmedName}
			subtitle={description}
			actions={actions}
			onClick={onClick}
			ariaPressed={
				typeof isSelected === 'boolean' && typeof onClick === 'function'
					? isSelected
					: undefined
			}
		/>
	)
}
